import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  DatePicker
} from '@mui/x-date-pickers/DatePicker';
import CourierPenalties from './CourierPenalties';
import {
  ArrowBack as ArrowBackIcon,
  Money as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import axiosInstance from '../utils/axios';
import * as XLSX from 'xlsx';

const CourierSalaries = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // Состояние для модального окна штрафа
  const [penaltyDialog, setPenaltyDialog] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [penaltyAmount, setPenaltyAmount] = useState('');
  const [penaltyComment, setPenaltyComment] = useState('');
  const [penaltyDate, setPenaltyDate] = useState(new Date());
  const [penaltyLoading, setPenaltyLoading] = useState(false);
  
  // Состояние для просмотра штрафов
  const [penaltiesView, setPenaltiesView] = useState({ open: false, courierId: null, courierName: null });
  const [selectedCourierPenalties, setSelectedCourierPenalties] = useState([]);
  
  // Состояние для раскрытия детальной информации по фасовкам
  const [expandedCouriers, setExpandedCouriers] = useState(new Set());

  const fetchSalaries = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (startDate) {
        params.append('startDate', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        params.append('endDate', format(endDate, 'yyyy-MM-dd'));
      }

      const response = await axiosInstance.get(`/courier-stats/salaries?${params.toString()}`);
      setData(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalaries();
  }, [startDate, endDate]);

  const handleDateFilter = () => {
    fetchSalaries();
  };

  const handleClearFilter = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const handleAddPenalty = (courier) => {
    setSelectedCourier(courier);
    setPenaltyAmount('');
    setPenaltyComment('');
    setPenaltyDate(new Date());
    setPenaltyDialog(true);
  };

  const handleViewPenalties = (courier) => {
    setPenaltiesView({
      open: true,
      courierId: courier.courierId,
      courierName: courier.courierName
    });
  };

  const handleSubmitPenalty = async () => {
    if (!penaltyAmount || !penaltyComment) {
      setError('Необходимо указать сумму и комментарий');
      return;
    }

    setPenaltyLoading(true);
    try {
      await axiosInstance.post('/courier-stats/penalty', {
        courierId: selectedCourier.courierId,
        amount: parseFloat(penaltyAmount),
        comment: penaltyComment,
        penaltyDate: format(penaltyDate, 'yyyy-MM-dd')
      });

      setPenaltyDialog(false);
      fetchSalaries(); // Обновляем данные
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка при добавлении штрафа');
    } finally {
      setPenaltyLoading(false);
    }
  };

  const handleCancelPenalty = async (penaltyId, reason) => {
    try {
      await axiosInstance.put(`/courier-stats/penalty/${penaltyId}/cancel`, {
        cancellationReason: reason
      });
      fetchSalaries(); // Обновляем данные
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка при аннулировании штрафа');
    }
  };

  const exportToExcel = () => {
    if (!data || !data.couriers) return;

    // Подготавливаем данные для экспорта
    const exportData = data.couriers.map((courier, index) => ({
      '№': index + 1,
      'Курьер': courier.courierName,
      'Telegram': courier.courierTelegram || '-',
      'Успешных заказов': courier.successfulOrders,
      'Ненаходы': courier.notFoundOrders,
      'Всего заказов': courier.totalOrders,
      'Зарплата (руб)': courier.totalSalary,
      'Штрафы из фасовки (руб)': courier.packagingPenalty,
      'Дополнительные штрафы (руб)': courier.additionalPenalty,
      'Общие штрафы (руб)': courier.totalPenalty,
      'Итоговая зарплата (руб)': courier.finalSalary,
      'Выручка (руб)': courier.totalRevenue,
      '% ЗП от выручки': `${courier.salaryPercentage}%`
    }));

    // Добавляем строку с общей статистикой
    exportData.push({});
    exportData.push({
      '№': '',
      'Курьер': 'ИТОГО:',
      'Telegram': '',
      'Успешных заказов': data.couriers.reduce((sum, c) => sum + c.successfulOrders, 0),
      'Ненаходы': data.couriers.reduce((sum, c) => sum + c.notFoundOrders, 0),
      'Всего заказов': data.couriers.reduce((sum, c) => sum + c.totalOrders, 0),
      'Зарплата (руб)': data.totalStats.totalSalarySum,
      'Штрафы из фасовки (руб)': data.couriers.reduce((sum, c) => sum + c.packagingPenalty, 0),
      'Дополнительные штрафы (руб)': data.couriers.reduce((sum, c) => sum + c.additionalPenalty, 0),
      'Общие штрафы (руб)': data.totalStats.totalPenaltySum,
      'Итоговая зарплата (руб)': data.totalStats.totalFinalSalarySum,
      'Выручка (руб)': data.totalStats.totalRevenueSum,
      '% ЗП от выручки': `${data.totalStats.avgSalaryPercentage.toFixed(2)}%`
    });

    // Создаем рабочую книгу
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Зарплаты курьеров');

    // Настраиваем ширину колонок
    const colWidths = [
      { wch: 5 },   // №
      { wch: 20 },  // Курьер
      { wch: 15 },  // Telegram
      { wch: 12 },  // Успешных заказов
      { wch: 10 },  // Ненаходы
      { wch: 12 },  // Всего заказов
      { wch: 15 },  // Зарплата
      { wch: 20 },  // Штрафы из фасовки
      { wch: 20 },  // Дополнительные штрафы
      { wch: 15 },  // Общие штрафы
      { wch: 18 },  // Итоговая зарплата
      { wch: 15 },  // Выручка
      { wch: 15 }   // % ЗП от выручки
    ];
    ws['!cols'] = colWidths;

    // Формируем имя файла с датами
    const periodStr = startDate && endDate 
      ? `_${format(startDate, 'dd.MM.yyyy', { locale: ru })}-${format(endDate, 'dd.MM.yyyy', { locale: ru })}`
      : startDate 
        ? `_с_${format(startDate, 'dd.MM.yyyy', { locale: ru })}`
        : endDate 
          ? `_до_${format(endDate, 'dd.MM.yyyy', { locale: ru })}`
          : '';
    
    const fileName = `Зарплаты_курьеров${periodStr}.xlsx`;

    // Сохраняем файл
    XLSX.writeFile(wb, fileName);
  };

  const toggleCourierExpansion = (courierId) => {
    const newExpanded = new Set(expandedCouriers);
    if (newExpanded.has(courierId)) {
      newExpanded.delete(courierId);
    } else {
      newExpanded.add(courierId);
    }
    setExpandedCouriers(newExpanded);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB'
    }).format(amount);
  };

  if (loading && !data) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>
          Загрузка данных о зарплатах...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/courier-stats')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" gutterBottom>
          Зарплаты курьеров
        </Typography>
        <Button
          variant="contained"
          color="success"
          onClick={exportToExcel}
          startIcon={<DownloadIcon />}
          disabled={!data || !data.couriers || data.couriers.length === 0}
        >
          Экспорт в Excel
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Фильтры по датам */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Фильтры
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Дата начала"
              value={startDate}
              onChange={setStartDate}
              renderInput={(params) => <TextField {...params} fullWidth />}
              format="dd.MM.yyyy"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Дата окончания"
              value={endDate}
              onChange={setEndDate}
              renderInput={(params) => <TextField {...params} fullWidth />}
              format="dd.MM.yyyy"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              onClick={handleDateFilter}
              disabled={loading}
              fullWidth
            >
              Применить
            </Button>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="outlined"
              onClick={handleClearFilter}
              disabled={loading}
              fullWidth
            >
              Сбросить
            </Button>
          </Grid>
        </Grid>
        {(startDate || endDate) && (
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Период: {startDate ? format(startDate, 'dd.MM.yyyy', { locale: ru }) : '...'} - {endDate ? format(endDate, 'dd.MM.yyyy', { locale: ru }) : '...'}
          </Typography>
        )}
      </Paper>

      {data && (
        <>
          {/* Общая статистика */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <MoneyIcon color="primary" sx={{ mr: 1 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Общая зарплата
                      </Typography>
                      <Typography variant="h6" component="div">
                        {formatCurrency(data.totalStats.totalFinalSalarySum)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <WarningIcon color="error" sx={{ mr: 1 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Общие штрафы
                      </Typography>
                      <Typography variant="h6" component="div">
                        {formatCurrency(data.totalStats.totalPenaltySum)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Общая выручка
                      </Typography>
                      <Typography variant="h6" component="div">
                        {formatCurrency(data.totalStats.totalRevenueSum)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <ReceiptIcon color="info" sx={{ mr: 1 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        % ЗП от выручки
                      </Typography>
                      <Typography variant="h6" component="div">
                        {data.totalStats.avgSalaryPercentage.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Таблица зарплат */}
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <Typography variant="h6" sx={{ p: 2 }}>
              Детализация по курьерам
            </Typography>
            
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Курьер</TableCell>
                    <TableCell align="right">Успешных</TableCell>
                    <TableCell align="right">Ненаходы</TableCell>
                    <TableCell align="right">Зарплата</TableCell>
                    <TableCell align="right">Штрафы</TableCell>
                    <TableCell align="right">Итого ЗП</TableCell>
                    <TableCell align="right">Выручка</TableCell>
                    <TableCell align="right">% ЗП</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.couriers.map((courier) => (
                    <React.Fragment key={courier.courierId}>
                      <TableRow hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {courier.courierName}
                            </Typography>
                            {courier.courierTelegram && (
                              <Typography variant="caption" color="textSecondary">
                                @{courier.courierTelegram}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <CheckCircleIcon color="success" sx={{ mr: 0.5 }} />
                            <Typography variant="body2" color="success.main">
                              {courier.successfulOrders}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <CancelIcon color="error" sx={{ mr: 0.5 }} />
                            <Typography variant="body2" color="error.main">
                              {courier.notFoundOrders}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(courier.totalSalary)}
                        </TableCell>
                        <TableCell align="right">
                          <Box>
                            <Typography variant="body2" color="error">
                              {formatCurrency(courier.totalPenalty)}
                            </Typography>
                            {courier.additionalPenalty > 0 && (
                              <Typography variant="caption" color="textSecondary">
                                (доп: {formatCurrency(courier.additionalPenalty)})
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            color={courier.finalSalary >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatCurrency(courier.finalSalary)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(courier.totalRevenue)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2"
                            color={courier.salaryPercentage >= 10 ? 'success.main' : 'warning.main'}
                          >
                            {courier.salaryPercentage.toFixed(2)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddPenalty(courier)}
                            >
                              Штраф
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => handleViewPenalties(courier)}
                            >
                              Штрафы
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={expandedCouriers.has(courier.courierId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              onClick={() => toggleCourierExpansion(courier.courierId)}
                            >
                              Детали
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                      
                      {/* Детальная информация по фасовкам */}
                      {expandedCouriers.has(courier.courierId) && courier.packagingDetails && (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ p: 0 }}>
                            <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                              <Typography variant="h6" gutterBottom>
                                Детализация по фасовкам - {courier.courierName}
                              </Typography>
                              
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Фасовка</TableCell>
                                      <TableCell align="right">Адресов</TableCell>
                                      <TableCell align="right">ЗП за адрес</TableCell>
                                      <TableCell align="right">Общая ЗП</TableCell>
                                      <TableCell align="right">Заказов</TableCell>
                                      <TableCell align="right">Успешных</TableCell>
                                      <TableCell align="right">Ненаходов</TableCell>
                                      <TableCell align="right">Штраф за ненаход</TableCell>
                                      <TableCell align="right">Общий штраф</TableCell>
                                      <TableCell align="right">% успешности</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {courier.packagingDetails.map((packaging, index) => (
                                      <TableRow key={index}>
                                        <TableCell>
                                          <Typography variant="body2" fontWeight="bold">
                                            {packaging.packagingName}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2">
                                            {packaging.addressCount}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2" color="success.main">
                                            {formatCurrency(packaging.paymentPerAddress)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2" fontWeight="bold" color="success.main">
                                            {formatCurrency(packaging.totalPayment)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2">
                                            {packaging.totalOrders}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2" color="success.main">
                                            {packaging.successfulOrders}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2" color="error.main">
                                            {packaging.notFoundOrders}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2" color="error.main">
                                            {formatCurrency(packaging.penaltyPerOrder)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2" fontWeight="bold" color="error.main">
                                            {formatCurrency(packaging.totalPenalty)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography 
                                            variant="body2" 
                                            color={packaging.successRate >= 80 ? 'success.main' : 'warning.main'}
                                          >
                                            {packaging.successRate.toFixed(1)}%
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Диалог добавления штрафа */}
      <Dialog open={penaltyDialog} onClose={() => setPenaltyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Выписать штраф курьеру: {selectedCourier?.courierName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <DatePicker
              label="Дата штрафа"
              value={penaltyDate}
              onChange={setPenaltyDate}
              renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
              format="dd.MM.yyyy"
            />
            <TextField
              label="Сумма штрафа"
              type="number"
              value={penaltyAmount}
              onChange={(e) => setPenaltyAmount(e.target.value)}
              fullWidth
              margin="normal"
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Комментарий"
              value={penaltyComment}
              onChange={(e) => setPenaltyComment(e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={3}
              placeholder="Укажите причину штрафа..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPenaltyDialog(false)}>
            Отмена
          </Button>
          <Button 
            onClick={handleSubmitPenalty}
            variant="contained"
            color="error"
            disabled={penaltyLoading || !penaltyAmount || !penaltyComment}
          >
            {penaltyLoading ? 'Сохранение...' : 'Выписать штраф'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог просмотра штрафов */}
      <CourierPenalties
        open={penaltiesView.open}
        courierId={penaltiesView.courierId}
        courierName={penaltiesView.courierName}
        onClose={(shouldRefresh) => {
          setPenaltiesView({ open: false, courierId: null, courierName: null });
          if (shouldRefresh) {
            fetchSalaries(); // Обновляем данные зарплат при изменении штрафов
          }
        }}
      />
    </Box>
  );
};

export default CourierSalaries;
